import {
  DEFAULT_OUTPUT_PROFILE,
  OUTPUT_PROFILES,
  outputProfileLabel,
  outputProfileOf,
  type OutputProfile,
} from '@/core/outputProfile';
import { SOLE_TRADER_CLIENT_TYPE, useClientType } from '@/core/useClientType';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  FilingStatusId,
  type DocumentVersionOption,
  type FilingAttemptHistoryRow,
  type FilingCheckRow,
  type FilingPreFlightReport,
  type PeriodOption,
} from '@/core/models/phase4.models';
import { ReportTypeCode, type ReportRow } from '@/core/models/reports.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { phase4Service } from '@/core/services/phase4';
import { reportsService } from '@/core/services/reports';
import { Button } from '@/kit/Button';
import { DataTable } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { Select, SelectOption } from '@/kit/Select';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { formatDateTime, useBusy, useClientGuard } from './shared';
import './TaxreturnFilingStatus.scss';

/** Only a filing Companies House has not decided on yet has a result still to get. */
export function canGetResult(row: FilingAttemptHistoryRow): boolean {
  return !!row.submissionId && (row.status === FilingStatusId.Submitted || row.status === FilingStatusId.Pending);
}

/**
 * AP-014 Filing: package pre-flight, the immutable approved filing copy, and the submission status / rejection /
 * acknowledgement history for a period.
 */
/** Names the kind of accounts when it is not the Full accounts: ` - Abridged accounts`. */
const profileSuffix = (row: { outputProfile?: string | null }) =>
  outputProfileOf(row) === DEFAULT_OUTPUT_PROFILE ? '' : ` - ${outputProfileLabel(row.outputProfile)}`;

export function TaxreturnFilingStatus() {
  const navigate = useNavigate();
  const client = useClientGuard();
  const { isBusy, begin, end } = useBusy();

  const [periods, setPeriods] = useState<PeriodOption[]>([]);
  const [documents, setDocuments] = useState<DocumentVersionOption[]>([]);
  /** Full, Abridged and Filleted accounts are filed separately, so the versions offered are of one kind. */
  const [outputProfile, setOutputProfile] = useState<OutputProfile>(DEFAULT_OUTPUT_PROFILE);
  const isSoleTrader = useClientType(client?.id) === SOLE_TRADER_CLIENT_TYPE;
  const profileDocuments = documents.filter((d) => outputProfileOf(d) === outputProfile);

  const filableFirst = (list: DocumentVersionOption[], profile: OutputProfile) =>
    // Only an approved or finalised version can be filed, so offer those first.
    list.find((d) => outputProfileOf(d) === profile && (d.status === 3 || d.status === 4))?.documentVersionId ?? null;

  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);

  const [preFlight, setPreFlight] = useState<FilingPreFlightReport | null>(null);
  const [attempts, setAttempts] = useState<FilingAttemptHistoryRow[]>([]);
  const [selectedAttempt, setSelectedAttempt] = useState<FilingAttemptHistoryRow | null>(null);

  /** #150: the annual accounts reports generated from this period's trial balances, listed for reference. */
  const [periodReports, setPeriodReports] = useState<ReportRow[]>([]);
  const reportsByPeriod = useRef(new Map<string, ReportRow[]>());
  // Angular set `selectedPeriodId` synchronously; the reports request reads it when it comes back.
  const currentPeriodId = useRef<string | null>(null);

  const chooseId = (periodId: string | null) => {
    currentPeriodId.current = periodId;
    setSelectedPeriodId(periodId);
  };

  const loadHistory = (periodId: string | null) => {
    if (!periodId) {
      return;
    }
    phase4Service.getFilingHistory(periodId).then(
      (result) => setAttempts(result.attempts),
      (error) => notifyError(error),
    );
  };

  /** #150: maps each annual report to its accounting period through its trial balance. */
  const loadReports = (clientId: string) => {
    Promise.all([
      reportsService.getReports(clientId, ReportTypeCode.Annual),
      reportsService.getTrialBalances(clientId),
    ]).then(
      ([reports, trialBalances]) => {
        const periodOf = new Map(trialBalances.map((t) => [t.id, t.accountingPeriodId]));
        const byPeriod = reportsByPeriod.current;
        byPeriod.clear();
        reports.forEach((r) => {
          const periodId = periodOf.get(r.currentTrialBalanceId);
          if (periodId) {
            byPeriod.set(periodId, [...(byPeriod.get(periodId) ?? []), r]);
          }
        });
        setPeriodReports(byPeriod.get(currentPeriodId.current ?? '') ?? []);
      },
      () => setPeriodReports([]),
    );
  };

  const onPeriodChange = (options: PeriodOption[], periodId: string | null) => {
    setPeriodReports(reportsByPeriod.current.get(periodId ?? '') ?? []);
    const period = options.find((p) => p.accountingPeriodId === periodId);
    const list = period ? period.documents : [];
    setDocuments(list);

    setSelectedDocumentId(filableFirst(list, outputProfile));

    setPreFlight(null);
    setSelectedAttempt(null);
    loadHistory(periodId);
  };

  useEffect(() => {
    if (!client) {
      return;
    }
    phase4Service.getPeriodOptions(client.id).then(
      (rows) => {
        setPeriods(rows);
        loadReports(client.id);
        if (rows.length > 0) {
          chooseId(rows[0].accountingPeriodId);
          onPeriodChange(rows, rows[0].accountingPeriodId);
        }
      },
      (error) => notifyError(error),
    );
    // Runs once, when the screen opens (ngOnInit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  /** #150/#151: where accounts versions for the period are generated and approved. */
  const openApproval = () => void navigate('/home/taxreturns/tasks-approval');

  /** The submission wizard runs the same pre-flight before it transmits. */
  const openSubmissionWizard = () => void navigate('/home/taxreturns/tasks-submit');

  const runPreFlight = () => {
    if (!selectedPeriodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    if (!selectedDocumentId) {
      notifyError('Select an accounts version first.');
      return;
    }
    if (!begin()) {
      return;
    }
    phase4Service
      .preFlight(selectedPeriodId, selectedDocumentId)
      .then(
        (report) => {
          setPreFlight(report);
          if (report.canSubmit) {
            notifySuccess('Pre-flight passed. The package is ready to file.');
          }
        },
        (error) => notifyError(error),
      )
      .finally(end);
  };

  /** Cuts the immutable approved copy: the exact bytes any submission will transmit. */
  const preparePackage = () => {
    if (!selectedPeriodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    if (!selectedDocumentId) {
      notifyError('Select an accounts version first.');
      return;
    }
    if (!begin()) {
      return;
    }
    phase4Service
      .preparePackage(selectedPeriodId, selectedDocumentId)
      .then(
        (row) => {
          notifySuccess(`Approved filing copy prepared (${row.contentLength} bytes).`);
          // Angular called runPreFlight() here while still busy (its `finalize` clears the flag afterwards), and
          // runPreFlight returns early when busy: the pre-flight does not actually run after preparing.
          runPreFlight();
        },
        (error) => notifyError(error),
      )
      .finally(end);
  };

  const resolve = (check: FilingCheckRow) => {
    if (check.route) {
      void navigate(check.route);
    }
  };

  const reportResult = (attempt: FilingAttemptHistoryRow) => {
    switch (attempt.status) {
      case FilingStatusId.Accepted:
        notifySuccess('Companies House has accepted the accounts.');
        break;
      case FilingStatusId.Rejected:
      case FilingStatusId.Failed:
        notifyError(
          `Companies House ${attempt.statusName.toLowerCase()} the accounts. See the History for the reasons.`,
        );
        break;
      default:
        notifySuccess('Companies House has not made a decision yet. Check again shortly.');
    }
  };

  /** Get the result: asks Companies House for its current decision instead of waiting for the background check. */
  const getResult = (row: FilingAttemptHistoryRow) => {
    if (!selectedPeriodId || !begin()) {
      return;
    }
    phase4Service
      .refreshFilingStatus(selectedPeriodId, row.filingAttemptId)
      .then(
        (updated) => {
          setAttempts((list) => list.map((a) => (a.filingAttemptId === updated.filingAttemptId ? updated : a)));
          setSelectedAttempt((selected) =>
            selected?.filingAttemptId === updated.filingAttemptId ? updated : selected,
          );
          reportResult(updated);
        },
        (error) => notifyError(error),
      )
      .finally(end);
  };

  const selectAttempt = (row: FilingAttemptHistoryRow) =>
    setSelectedAttempt((selected) => (selected === row ? null : row));

  return (
    <SideMenuPage menu="task" contentClassName="taxreturn-filing-status">
      {/* Design review 5 Nov 2026: the left-hand menu already names this page (Filing Status), so the title is hidden
          (accessible name only) and the period/version pickers join the actions row instead of sitting in a second row. */}
      <PageHeader title="Filing Status" hideTitle>
        <Select
          className="w-250"
          openOnFocus
          panelClass="myPanelClass"
          clearable={false}
          placeholder="Accounting period"
          value={selectedPeriodId}
          onChange={(value) => {
            const periodId = (value as string | null) ?? null;
            chooseId(periodId);
            onPeriodChange(periods, periodId);
          }}
        >
          {periods.map((option) => (
            <SelectOption key={option.accountingPeriodId} value={option.accountingPeriodId}>
              {option.label}
            </SelectOption>
          ))}
        </Select>
        <Select
          className="w-250"
          openOnFocus
          panelClass="myPanelClass"
          clearable={false}
          placeholder="Accounts version"
          value={selectedDocumentId}
          onChange={(value) => {
            setSelectedDocumentId((value as string | null) ?? null);
            setPreFlight(null);
          }}
        >
          {profileDocuments.map((option) => (
            <SelectOption key={option.documentVersionId} value={option.documentVersionId}>
              {option.label}
            </SelectOption>
          ))}
        </Select>
        {!isSoleTrader && (
          <Select
            className="w-200"
            openOnFocus
            panelClass="myPanelClass"
            clearable={false}
            value={outputProfile}
            onChange={(value) => {
              const profile = (value as OutputProfile | null) ?? DEFAULT_OUTPUT_PROFILE;
              setOutputProfile(profile);
              setSelectedDocumentId(filableFirst(documents, profile));
              setPreFlight(null);
            }}
          >
            {OUTPUT_PROFILES.map((option) => (
              <SelectOption key={option.code} value={option.code}>
                {option.label}
              </SelectOption>
            ))}
          </Select>
        )}
        <Button
          className="action-button primary-button"
          disabled={!selectedDocumentId || isBusy}
          onClick={runPreFlight}
        >
          <Icon name="fact_check" outlined />
          <span>Run pre-flight</span>
        </Button>
        <Button
          className="action-button secondary-button"
          disabled={!selectedDocumentId || isBusy}
          onClick={preparePackage}
        >
          <Icon name="inventory_2" outlined />
          <span>Prepare filing copy</span>
        </Button>
        <Button className="action-button primary-button" onClick={openSubmissionWizard}>
          <Icon name="send" />
          <span>Submit</span>
        </Button>
      </PageHeader>
      <div className="content-body">
        {/* #150/#151: filing works on an accounts version of the period (not the report previews under Reports), and
            pre-flight and the filing copy stay disabled until one is selected - say so and where to get one. */}
        {selectedPeriodId && !documents.length && (
          <p className="text-muted font-size-12 mb-1">
            This period has no accounts version to file yet. Accounts versions are generated and approved on{' '}
            <a className="link" onClick={openApproval}>
              Approval &amp; Signatories
            </a>{' '}
            (or iXBRL Tagging); the reports under Reports &gt; Annual Accounts are previews and are not listed here. Run
            pre-flight and Prepare filing copy become available once a version is selected.
          </p>
        )}

        {/* #150: the reports generated for this period, so it is clear which accounts the filing versions relate to. */}
        {selectedPeriodId && (
          <p className="font-size-12 mb-1">
            <span className="fw-bold">Accounts reports for this period:</span>
            {periodReports.length ? (
              periodReports.map((r, index) => (
                <span key={r.id}>
                  {r.refNo} ({r.type}){index === periodReports.length - 1 ? '' : ', '}
                </span>
              ))
            ) : (
              <> none generated yet.</>
            )}
          </p>
        )}

        {preFlight && (
          <div className="sub-wrap">
            <div className="sub-acc">
              <div className="sub-ttl">Version :</div>
              <div className="sub-dtl">
                v{preFlight.documentVersionNo} ({preFlight.documentStatusName}){profileSuffix(preFlight)}
              </div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Company number :</div>
              <div className="sub-dtl">{preFlight.companyNumber || '-'}</div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Auth code :</div>
              <div className={`sub-dtl ${preFlight.hasAuthenticationCode ? 'text-green' : 'text-red'}`}>
                {preFlight.hasAuthenticationCode ? 'Held' : 'Missing'}
              </div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Taxonomy :</div>
              <div className="sub-dtl">
                {preFlight.taxonomyName || '-'} {preFlight.taxonomyVersion}
              </div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Facts :</div>
              <div className="sub-dtl">{preFlight.factCount}</div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Filing copy :</div>
              <div className="sub-dtl">
                {preFlight.hasFilingPackage ? preFlight.packageContentHash : 'Not prepared'}
              </div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Result :</div>
              <div className={`sub-dtl ${preFlight.canSubmit ? 'text-green' : 'text-red'}`}>
                {preFlight.canSubmit ? 'Ready to file' : `${preFlight.blockingCount} blocking issue(s)`}
              </div>
            </div>
          </div>
        )}

        {preFlight && (
          <div className="mat-table-wrapper mb-2">
            <DataTable
              stickyHeader
              rows={preFlight.checks}
              rowKey={(row, index) => `${row.srNo}-${index}`}
              columns={[
                { id: 'srno', header: 'Sr. No', cell: (c) => c.srNo },
                { id: 'check', header: 'Check', cell: (c) => c.check },
                {
                  id: 'severity',
                  header: 'Severity',
                  cellClassName: (c) => (c.isBlocking ? 'text-red' : undefined),
                  cell: (c) => c.severity,
                },
                { id: 'message', header: 'Message', cell: (c) => c.message },
                {
                  id: 'link',
                  header: 'Link',
                  cell: (c) =>
                    c.route && (
                      <a className="link" onClick={() => resolve(c)}>
                        Resolve
                      </a>
                    ),
                },
              ]}
            />
          </div>
        )}

        <div className="form-field">
          <p>Submission history</p>
        </div>

        <div className="mat-table-wrapper">
          <DataTable
            stickyHeader
            rows={attempts}
            rowKey={(row) => row.filingAttemptId}
            columns={[
              {
                id: 'prepared',
                header: 'Prepared',
                cell: (a) => (
                  <>
                    {formatDateTime(a.preparedAtUtc)}
                    <div className="text-gray">{a.preparedByName}</div>
                  </>
                ),
              },
              { id: 'version', header: 'Version', cell: (a) => `v${a.documentVersionNo}${profileSuffix(a)}` },
              {
                id: 'status',
                header: 'Status',
                cellClassName: (a) => (a.status === 4 || a.status === 5 ? 'text-red' : undefined),
                cell: (a) => a.statusName,
              },
              { id: 'submission', header: 'Submission', cell: (a) => a.submissionId || '-' },
              { id: 'response', header: 'Response', cell: (a) => a.responseRef || '-' },
              {
                id: 'hash',
                header: 'Package hash',
                cell: (a) => a.packageContentHash || a.documentContentHash || '-',
              },
              {
                id: 'action',
                header: 'Detail',
                cell: (a) => (
                  <>
                    <a className="link" onClick={() => selectAttempt(a)}>
                      History
                    </a>
                    {canGetResult(a) && (
                      <a className="link ml-1" onClick={() => getResult(a)}>
                        Get result
                      </a>
                    )}
                  </>
                ),
              },
            ]}
          />
        </div>

        {selectedAttempt && (
          <div className="note-box mt-2">
            <div className="form-field">
              <p>Attempt {selectedAttempt.submissionId || selectedAttempt.filingAttemptId}</p>
            </div>
            <ul>
              {selectedAttempt.timeline.map((entry, index) => (
                <li key={index}>
                  {formatDateTime(entry.occurredAtUtc)} - {entry.status}: {entry.description}
                </li>
              ))}
            </ul>
            {selectedAttempt.responses.length > 0 && (
              <div className="form-field">
                <p>Regulator messages</p>
              </div>
            )}
            <ul>
              {selectedAttempt.responses.map((response, index) => (
                <li key={index} className={response.severity === 2 ? 'text-red' : ''}>
                  {formatDateTime(response.receivedAtUtc)} - {response.responseType}{' '}
                  {response.code && <span>({response.code})</span>}: {response.message}{' '}
                  {response.isSandbox && <span className="text-gray">[sandbox]</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </SideMenuPage>
  );
}
